import React, { useContext, useState } from "react";
import { TextField } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import { reverseGeoCoding } from "@/utils/reverseGeoCoding";
import { IconBtn } from "../../../atom/IconBtn/IconBtn";
import { validateEmail, validateMobile, validateISDNumber } from "../../../constants/functions";
import { Cross, Facebook, Google, Apple } from "../../../constants/icons";
import {
  DEBOUNCE_DELAY,
  SIGN_IN_OTP_PAGE,
  SIGN_UP_MODULE,
  DEFAULT_COUNTRY_CODE,
  SIGN_IN_WITH_PASSWORD_PAGE
} from "../../../constants/variables";
import { useAuth } from "../../../context/AuthContext";
import { CommonDataContext } from "../../../context/CommonDataContext";
import { fetchCMSConfig } from "../../../dataLayer/cms";
import { extractResource } from "../../../helper/extractResource";
import { Button } from "../../UI/Button";
import TncConcent from "../tncConcent";
import { useSelector } from "react-redux";
import { getIsDarkMode } from "@/store/slices/webConfig";
import 'react-phone-input-2/lib/material.css'

function SignInLandingPage(props) {
  const [timerId, setTimerId] = React.useState<any>(null);
  const [error, setError] = React.useState(false);
  const { sendOtp, loginWithGoogle, loginWithApple, loginWithFacebook } = useAuth();
  const [configData, setConfigData]: any = React.useState({});
  const { resourceData, setEmailOrMobile } = props;
  const [ISDcountryCode, setISDcountryCode] = React.useState("in");
  const [phoneErr, setPhoneErr] = useState(false);
  const { webConfig }: any = useContext(CommonDataContext);

  const enableInternationalFlights = webConfig?.enableInternationalFlights;
  const enableDetectGeoLocation = webConfig?.enableInternationalFlights ?? false;
  const isDarkMode = useSelector(getIsDarkMode);

  // ? the function validates email or mobile number
  // ? sets errors if invalid
  const main = (e: any) => {
    if (isNaN(+props.emailOrMobile)) {
      if (validateEmail(e?.target?.value)) {
        setError(false);
      } else {
        setError(true);
      }
    } else if (e?.target?.value?.length !== 10) {
      setError(true);
    }
  };

  // ? the debounce function calls the main fucntion 1s after user stops typing
  const debounce = (e: any, delay: any) => {
    setError(false);

    if (timerId) {
      clearTimeout(timerId);
      setTimerId(null);
    }

    setTimerId(
      setTimeout(() => {
        main(e);
      }, delay)
    );
  };

  // ? the function updates the emailOrMobile state with current input value
  // ? it also triggers the debounce function

  const handleChange = (e) => {
    props.setEmailOrMobile(e.target.value);

    debounce(e, DEBOUNCE_DELAY);
  };

  const handleChangeISD = (inputNumber: string) => {
    if (validateISDNumber(inputNumber)) {
      setPhoneErr(false);
    } else {
      setPhoneErr(true);
    }
    setEmailOrMobile(inputNumber);

    debounce(inputNumber, DEBOUNCE_DELAY);
  };

  // ? the function validates email or mobile number before submission
  // ? it sets errors if invalid
  // ? if valid, it allows the action call
  const handleSubmitISD = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validateMobile(props.emailOrMobile)) {
      sendOtp("+" + props.emailOrMobile);
      props.handleState(SIGN_IN_OTP_PAGE); // sending user to the otp flow
    } else setError(true);

    sendOtp("+" + props.emailOrMobile);
    props.handleState(SIGN_IN_OTP_PAGE);
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validateMobile(props.emailOrMobile)) {
      sendOtp(DEFAULT_COUNTRY_CODE + props.emailOrMobile);
      props.handleState(SIGN_IN_OTP_PAGE); // sending user to the otp flow
    } else setError(true);
  };

  const handelLoginWithGoogle = async (e: any) => {
    e.preventDefault();
    try {
      await loginWithGoogle();
    } catch (err) {
      console.warn("Error in login with google:", err);
    }
  };

  const handelLoginWithFacebook = async (e: any) => {
    e.preventDefault();
    try {
      await loginWithFacebook();
    } catch (err) {
      console.warn("Error in login with fb:", err);
    }
  };

  const handelLoginWithApple = async (e: any) => {
    e.preventDefault();
    try {
      await loginWithApple();
    } catch (err) {
      console.warn("Error in login with apple:", err);
    }
  };

  React.useEffect(() => {
    const getConfigKeys = async () => {
      const res = await fetchCMSConfig();
      const newConfigData = res?.data?.story?.content?.config[0];
      setConfigData(newConfigData);
    };
    getConfigKeys();
  }, []);

  const fetchCountryCode = () => {
    if (enableDetectGeoLocation) {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          try {
            const { latitude } = position.coords;
            const { longitude } = position.coords;

            const userLocation = await reverseGeoCoding([longitude, latitude]);
            setISDcountryCode(userLocation);
          } catch (errorCode) {
            console.warn(`Error fetching country ISD Code: ${errorCode}`);
          }
        });
      } else {
        console.warn("Geolocation API is not available in this browser.");
      }
    }
  };

  React.useEffect(() => {
    fetchCountryCode();
  }, []);

  // ? returns landing page of sign in module
  return (
    <section className="w-full sm:w-80 m-auto h-full flex flex-col justify-between">
      <div className="w-full h-full">
        <div className="flex justify-between items-center text-typography-primary">
          <h1 className="font-semibold text-xl">{extractResource(resourceData, "signIn")}</h1>
          <IconBtn
            onClick={() => {
              // closes the drawer
              props.handleOpen(false);
            }}
            aria-label={extractResource(resourceData, "closeText")}
            title={extractResource(resourceData, "closeText")}
            type="button"
          >
            <Cross fill="inherit" />
          </IconBtn>
        </div>

        <form onSubmit={enableInternationalFlights ? handleSubmitISD : handleSubmit} name="signInWithOtp">
          {enableInternationalFlights ? (
            <div className="mt-2 mb-2">
              <PhoneInput
                country={ISDcountryCode}
                specialLabel={extractResource(resourceData, "phone")}
                inputProps={{
                  name: "phone",
                  required: true,
                  maxLength:17,

                }}
                onChange={handleChangeISD}
                defaultMask="..................................................................................................."
                enableLongNumbers
                alwaysDefaultMask
                inputStyle={{
                  width: "100%",
                  height: "46px",
                  borderRadius: "8px 0px 8px 8px",
                  color: "black"
                }}
                isValid={(value, country: any) => {
                  if (phoneErr) {
                    return extractResource(resourceData, "phone");
                  }
                  return true;
                }}
              />
              <p className="text-typography-error text-xs mx-3 mt-1">
                {phoneErr ? extractResource(resourceData, "invalidPhone") : ""}
              </p>
            </div>
          ) : (
            <TextField
              error={error}
              id="emailOrMobile"
              label={extractResource(resourceData, "phone")}
              defaultValue={props.emailOrMobile}
              helperText={error ? resourceData?.invalidMobileNumber : ""}
              onChange={handleChange}
              name="emailOrMobile"
              fullWidth
              sx={{
                margin: error ? "24px 0px 0px 0px" : "24px 0px"
              }}
            />
          )}

          <Button
            fullWidth
            type="submit"
            color="primary"
            variant="contained"
            aria-label={extractResource(resourceData, "signInWithOtp")}
          >
            {extractResource(resourceData, "signInWithOtp")}
          </Button>
        </form>

        <div className="flex my-5">
          <hr className="h-0.2 w-auto flex-auto flex-grow-1 bg-gray-200 mt-3" />
          <p className="mx-3 text-gray-500 font-medium dark:text-gray-100">{extractResource(resourceData, "or")}</p>
          <hr className="h-0.2 w-auto flex-auto flex-grow-1 bg-gray-200 mt-3" />
        </div>

        <Button
          className="m-auto w-full"
          onClick={() => {
            // sets the state to loginInWithPassword in order to go to sign in with password screen
            props.handleState(SIGN_IN_WITH_PASSWORD_PAGE);
          }}
          type="button"
          color="secondary"
          variant="text"
          aria-label={extractResource(resourceData, "signInWithPassword")}
        >
          {extractResource(resourceData, "signInWithPassword")}
        </Button>

        <p className="w-full mx-auto mt-16 mb-3 font-medium text-center">
          {extractResource(resourceData, "signUpForFasterCheckout")}
        </p>
        <Button
          className="w-full px-6 my-5"
          color="default"
          type="button"
          variant="outlined"
          onClick={() => {
            props.handleModule(SIGN_UP_MODULE);
          }}
        >
          {extractResource(resourceData, "signUp")}
        </Button>
        {configData?.socialLogin ? (
          <div className="flex w-fit mx-auto justify-center mt-2 dark:grayscale">
            <button type="button" onClick={handelLoginWithFacebook}>
              <Facebook className="shadow" />
            </button>
            <button type="button" onClick={handelLoginWithGoogle}>
              <Google className="shadow" />
            </button>
            <button type="button" onClick={handelLoginWithApple}>
              <Apple className="shadow" />
            </button>
          </div>
        ) : (
          ""
        )}
      </div>

      <div className="w-full flex flex-col justify-center">
        <div className="w-full text-center relative bottom-0 left-0">
          <Button
            variant="text"
            color="secondary"
            fullWidth
            onClick={() => {
              // closes the drawer
              props.handleOpen(false);
            }}
            type="button"
          >
            {extractResource(resourceData, "continueAsGuest")}
          </Button>
        </div>

        <div className="w-fit mx-auto">
          <TncConcent resourceData={resourceData} />
        </div>
      </div>
    </section>
  );
}

export default SignInLandingPage;
function aynsc() {
  throw new Error("Function not implemented.");
}
